import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../../api/axios';
import { ArrowLeft, Edit, CreditCard, Printer } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import { useOrganization } from '../../hooks/useOrganization';
import { formatDate } from '../../utils/dateFormat';

const InvoiceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const [invoice, setInvoice] = useState(null);
  const [quotation, setQuotation] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoice();
    fetchSettings();
  }, [id]);

  useEffect(() => {
    if (invoice) {
      document.title = `Invoice-${invoice.invoiceNumber || `INV-${invoice.id}`}`;
    }
    return () => {
      document.title = 'AGMS - Auto Garage Management System';
    };
  }, [invoice]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`/api/invoices/${id}`);
      const invoiceData = response.data;
      setInvoice(invoiceData);
      
      if (invoiceData.quotationId) {
        try {
          const quotationResponse = await axios.get(`/api/quotations/${invoiceData.quotationId}`);
          setQuotation(quotationResponse.data);
        } catch (quotationError) {}
      }
    } catch (error) {
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/settings/config');
      setSettings(response.data);
    } catch (error) {}
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!invoice) return <div className="p-6">Invoice not found</div>;

  const subtotal = parseFloat(invoice.subtotal) || 0;
  const discount = parseFloat(invoice.discount) || 0;
  const tax = parseFloat(invoice.tax) || 0;
  const total = parseFloat(invoice.total) || 0;
  const vatPercentage = tax > 0 && subtotal > discount
    ? Math.round((tax / (subtotal - discount)) * 100 * 100) / 100
    : 0;

  const itemCount = Array.isArray(invoice.items) ? invoice.items.length : 0;
  const getFontScale = () => {
    if (itemCount <= 2) return 1.15;
    if (itemCount <= 4) return 1.05;
    if (itemCount <= 6) return 0.98;
    if (itemCount <= 8) return 0.92;
    if (itemCount <= 10) return 0.88;
    if (itemCount <= 12) return 0.84;
    if (itemCount <= 14) return 0.8;
    if (itemCount <= 16) return 0.76;
    if (itemCount <= 18) return 0.72;
    if (itemCount <= 20) return 0.68;
    if (itemCount <= 22) return 0.64;
    if (itemCount <= 25) return 0.6;
    if (itemCount <= 28) return 0.56;
    if (itemCount <= 30) return 0.52;
    return 0.48;
  };
  const fontScale = getFontScale();

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{`
        @media print {
          .dynamic-scale {
            --font-scale: ${fontScale};
          }
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { 
            position: absolute; left: 0; top: 0; width: 100%;
          }
          .no-print { display: none !important; }
          @page { 
            size: A4;
            margin: 6mm 10mm 10mm 10mm;
          }
          .print-header { 
            margin-bottom: 4px;
            padding-bottom: 4px;
            border-bottom: 1.5px solid #333;
            page-break-inside: avoid;
          }
          .print-logo { 
            height: calc(55px * var(--font-scale, 1));
            width: calc(55px * var(--font-scale, 1));
            object-fit: contain;
            border-radius: 6px !important;
          }
          .print-section { 
            margin-bottom: 2px;
            padding: 0px;
            line-height: 1.2;
          }
          .print-section h1 {
            font-size: calc(16px * var(--font-scale, 1)) !important;
            margin: 0 0 1px 0 !important;
            font-weight: bold;
          }
          .print-section h2 {
            font-size: calc(12px * var(--font-scale, 1)) !important;
            margin: 0 !important;
            font-weight: bold;
          }
          .print-section p {
            font-size: calc(9px * var(--font-scale, 1)) !important;
            margin: 0 !important;
            line-height: 1.3;
          }
          .print-grid { 
            grid-template-columns: 1fr 1fr 1fr !important;
            gap: 6px !important;
            margin-top: 4px !important;
            margin-bottom: 6px !important;
            padding: 2px 0;
          }
          .print-details { line-height: 1.2; }
          .print-details > div { 
            margin: 0 0 2px 0;
            font-size: calc(10px * var(--font-scale, 1));
            line-height: 1.4;
          }
          .print-table { 
            width: 100%; 
            border-collapse: collapse;
            margin: 4px 0 !important;
          }
          .print-table thead { display: table-header-group; }
          .print-table tbody { display: table-row-group; }
          .print-table tr { page-break-inside: avoid; }
          .print-table th {
            border: 1px solid #555 !important;
            padding: calc(4px * var(--font-scale, 1)) calc(5px * var(--font-scale, 1)) !important;
            font-size: calc(9px * var(--font-scale, 1)) !important;
            line-height: 1.3;
            background-color: #f0f0f0 !important;
            font-weight: bold;
            text-transform: uppercase;
            color: #333;
          }
          .print-table td { 
            border: 1px solid #999 !important;
            padding: calc(4px * var(--font-scale, 1)) calc(5px * var(--font-scale, 1)) !important;
            font-size: calc(10px * var(--font-scale, 1)) !important;
            line-height: 1.3;
          }
          .print-totals { 
            margin-top: calc(16px * var(--font-scale, 1)) !important;
            display: flex;
            justify-content: flex-end;
            page-break-inside: avoid;
          }
          .print-totals > div:first-child { display: none; }
          .print-totals .w-64 {
            font-size: calc(12px * var(--font-scale, 1)) !important;
            line-height: 1.4;
            padding: calc(8px * var(--font-scale, 1));
            border: 1px solid #ddd;
            border-radius: 4px;
            min-width: 220px;
          }
          .print-totals .w-64 > div {
            padding: calc(2px * var(--font-scale, 1)) 0;
          }
          .print-terms { 
            font-size: 7px !important;
            line-height: 1.3;
            padding: 2px 0;
            margin-top: 4px;
          }
          .print-terms p { margin: 1px 0; font-size: 7px !important; }
          .print-signature { 
            margin-top: auto;
            padding-top: 20px;
            padding-bottom: 5px;
            page-break-inside: avoid;
          }
          .print-signature p {
            font-size: 11px !important;
            margin-top: 3px;
            font-weight: 500;
          }
          .page-footer {
            position: fixed;
            bottom: 3mm;
            left: 10mm;
            right: 10mm;
            text-align: center;
            font-size: 8px;
            color: #555;
            border-top: 1px solid #ccc;
            padding-top: 3px;
          }
          h3 {
            font-size: calc(10px * var(--font-scale, 1)) !important;
            margin-bottom: 2px !important;
            margin-top: 4px !important;
            font-weight: bold;
          }
          .print-area > div { padding: 0 !important; }
          .space-y-6 > * + * { margin-top: 4px !important; }
        }
      `}</style>
    <div className="p-6 print-area dynamic-scale">
      {/* Print Header */}
      <div className="print-header hidden print:block">
        <div className="flex justify-between items-center print-section">
          <div className="flex items-center gap-4">
            {organization?.logoUrl && (
              <img src={organization.logoUrl} alt="Company Logo" className="print-logo" />
            )}
            <div>
              <h1>{organization?.companyName || 'Company'}</h1>
              <p>{organization?.address}{organization?.city ? `, ${organization.city}` : ''}</p>
              <p>{organization?.companyContact} | {organization?.email}</p>
              {organization?.vatId && <p>TRN: {organization.vatId}</p>}
            </div>
          </div>
          <div className="text-right">
            <h2>{settings?.invoiceHeading || 'INVOICE'}</h2>
            <p className="font-bold">#{invoice.invoiceNumber || `INV-${invoice.id}`}</p>
            <p>Date: {formatDate(invoice.invoiceDate)}</p>
            <p>Due: {formatDate(invoice.dueDate)}</p>
          </div>
        </div>
      </div>

      {/* Print 3-Column Details */}
      <div className="grid grid-cols-3 gap-4 print-grid items-start hidden print:grid">
        <div>
          <h3 className="text-lg font-semibold mb-2">Bill To:</h3>
          <div className="space-y-2 text-sm print-details">
            <div>{invoice.customer?.name}</div>
            <div>{invoice.customer?.contact || 'N/A'}</div>
            <div>{invoice.customer?.email || 'N/A'}</div>
          </div>
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-2">Invoice Details</h3>
          <div className="space-y-2 text-sm print-details">
            <div><span className="font-medium">Branch:</span> {invoice.branch?.name}</div>
            <div><span className="font-medium">Status:</span> {invoice.paymentStatus}</div>
            {quotation && <div><span className="font-medium">Quotation:</span> {quotation.quotationNumber}</div>}
          </div>
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-2">Vehicle Details</h3>
          <div className="space-y-2 text-sm print-details">
            <div><span className="font-medium">Vehicle:</span> {invoice.vehicleNumber || 'N/A'}</div>
            <div><span className="font-medium">VIN:</span> {invoice.vinNumber || 'N/A'}</div>
            <div><span className="font-medium">Odometer:</span> {invoice.odometerReading || 'N/A'}</div>
          </div>
        </div>
      </div>

      {/* Screen Header */}
      <div className="flex items-center justify-between mb-6 no-print">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/accounts/invoices')} className="flex items-center text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Invoices
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Invoice #{invoice.invoiceNumber || `INV-${invoice.id}`}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={handlePrint} className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 flex items-center gap-2">
            <Printer className="w-4 h-4" /> Print
          </button>
          {invoice.paymentStatus !== 'Paid' && (
            <Link to={`/accounts/invoices/${invoice.id}/edit`} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2">
              <Edit className="w-4 h-4" /> Edit
            </Link>
          )}
          {invoice.paymentStatus !== 'Paid' && (
            <Link to={`/accounts/payments/create?invoiceId=${invoice.id}`} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Record Payment
            </Link>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-6 print:shadow-none print:border-0">
        {/* Screen-only details */}
        <div className="grid grid-cols-3 gap-6 no-print">
          <div>
            <h3 className="text-lg font-semibold mb-3">Bill To:</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">Name:</span> {invoice.customer?.name}</div>
              <div><span className="font-medium">Contact:</span> {invoice.customer?.contact || 'N/A'}</div>
              <div><span className="font-medium">Email:</span> {invoice.customer?.email || 'N/A'}</div>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-3">Invoice Details</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">Date:</span> {formatDate(invoice.invoiceDate)}</div>
              <div><span className="font-medium">Due:</span> {formatDate(invoice.dueDate)}</div>
              <div><span className="font-medium">Branch:</span> {invoice.branch?.name}</div>
              <div><span className="font-medium">Status:</span> {invoice.paymentStatus}</div>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-3">Vehicle Details</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">Vehicle:</span> {invoice.vehicleNumber || 'N/A'}</div>
              <div><span className="font-medium">VIN:</span> {invoice.vinNumber || 'N/A'}</div>
              <div><span className="font-medium">Odometer:</span> {invoice.odometerReading || 'N/A'}</div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="print:mt-0">
          <h3 className="text-lg font-semibold mb-1 print:mb-0">Items</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 print-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">SN</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(Array.isArray(invoice.items) ? invoice.items : []).map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm text-center">{index + 1}</td>
                    <td className="px-4 py-3 text-sm">{item.description || item.name}</td>
                    <td className="px-4 py-3 text-sm text-center">{item.quantity}</td>
                    <td className="px-4 py-3 text-sm text-center">{formatCurrency(parseFloat(item.price) || 0, organization?.currency_code)}</td>
                    <td className="px-4 py-3 text-sm text-center">{formatCurrency(item.total || (item.price * item.quantity) || 0, organization?.currency_code)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-between print-totals">
          <div className="w-1/2 pr-4"></div>
          <div className="w-64 space-y-2 text-sm ml-auto">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>{formatCurrency(subtotal, organization?.currency_code)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between">
                <span>Discount:</span>
                <span>{formatCurrency(discount, organization?.currency_code)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Tax ({Number(vatPercentage).toFixed(0)}%):</span>
              <span>{formatCurrency(tax, organization?.currency_code)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total:</span>
              <span>{formatCurrency(total, organization?.currency_code)}</span>
            </div>
            {parseFloat(invoice.paidAmount || 0) > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Paid:</span>
                <span>{formatCurrency(parseFloat(invoice.paidAmount), organization?.currency_code)}</span>
              </div>
            )}
            {parseFloat(invoice.paidAmount || 0) > 0 && parseFloat(invoice.paidAmount) < total && (
              <div className="flex justify-between font-bold text-red-600 border-t pt-1">
                <span>Balance Due:</span>
                <span>{formatCurrency(total - parseFloat(invoice.paidAmount), organization?.currency_code)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Terms & Conditions */}
        <div>
          <h3 className="text-lg font-semibold mb-2">Terms & Conditions</h3>
          <div className="text-sm text-gray-600 print-terms">
            {invoice.termsConditions ? (
              <div className="whitespace-pre-line">{invoice.termsConditions}</div>
            ) : (
              <div>
                {invoice.paymentStatus !== 'Paid' && (
                  <p className="font-medium mb-1">Payment is due by {formatDate(invoice.dueDate)}.</p>
                )}
                <p><strong>Payment Terms</strong> – Payment is due within the specified due date mentioned above.</p>
                <p><strong>Late Payment</strong> – Late payment charges may apply for overdue invoices.</p>
                <p><strong>Warranty</strong> – Warranty applies only to parts and labor as specified in the original quotation.</p>
                <p><strong>Disputes</strong> – Any disputes regarding this invoice must be raised within 7 days of receipt.</p>
                <p><strong>Services Rendered</strong> – This invoice represents services and parts provided as agreed.</p>
                <p><strong>Liability</strong> – The company is not liable for delays caused by parts unavailability or unforeseen issues.</p>
              </div>
            )}
          </div>
        </div>

        {/* Signature - Print Only */}
        <div className="hidden print:block print-signature">
          <div className="flex justify-between" style={{ marginTop: '40px' }}>
            <div className="border-t border-black pt-2 w-48">
              <p className="text-center" style={{ fontSize: '11px' }}>Authorized Signature</p>
            </div>
            <div className="border-t border-black pt-2 w-48">
              <p className="text-center" style={{ fontSize: '11px' }}>Customer Signature & Date</p>
            </div>
          </div>
        </div>

        {/* Footer - Print Only */}
        <div className="page-footer">
          {organization?.companyName && <span>{organization.companyName}</span>}
          {organization?.companyContact && <span> | {organization.companyContact}</span>}
          {organization?.email && <span> | {organization.email}</span>}
          {organization?.address && <span> | {organization.address}</span>}
          {organization?.city && <span>, {organization.city}</span>}
          {organization?.country && <span>, {organization.country}</span>}
        </div>
      </div>
    </div>
    </>
  );
};

export default InvoiceView;
